import { History as HistoryIcon } from "@mui/icons-material";
import { Grid, Link, ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";
import { DetailAccordion } from "components/DetailAccordion";
import { intlFormatDate } from "helpers";
import { formatCustomProperty } from "helpers/formatCustomProperty";
import { FC } from "react";
import { useIntl } from "react-intl";
import { useAppSelector } from "store";
import { selectPageSelectedJob } from "store/root.store";
import { isFlagEnabled } from "store/slices/user.store";

const PREFIX = "JobDetailTab";

const classes = {
  bold: `${PREFIX}-bold`,
  historyIconAdjust: `${PREFIX}-eq-history-icon-adjust`,
  iconAutoMargin: `${PREFIX}-icon-auto-margin`,
  noPadding: `${PREFIX}-no-padding`,
  leftIndent: `${PREFIX}-left-indent`,
  disabled: `${PREFIX}-disabled`,
};

interface MachineDisplayProps {
  planner?: boolean;
}

export const ReadOnlyMachineDisplay: FC<MachineDisplayProps> = (props: MachineDisplayProps) => {
  const { planner } = props;
  const intl = useIntl();
  const job = useAppSelector(selectPageSelectedJob);
  const { machineProperties } = useAppSelector((s) => s.cache);
  const machineSortGroupsEnabled = useAppSelector((s) => isFlagEnabled(s, "MachineSortGroups"));
  const machineInstallDate = job.machine?.installDate;

  const historyIcon = (
    <HistoryIcon className={`${classes.historyIconAdjust} ${classes.disabled}`} />
  );

  return (
    <>
      <ListItem
        component="div"
        disableGutters
        className={classes.noPadding}
        data-testid="MachineDisplay"
      >
        {job.machine?.id && !planner && (
          <ListItemIcon
            className={`min-width-38 ${classes.iconAutoMargin}`}
            sx={{ alignSelf: "flex-start", mt: 1 }}
            data-testid="MachineDisplay-OpenMachineHistory"
          >
            {historyIcon}
          </ListItemIcon>
        )}
        <ListItemText className={classes.bold} primary={<>{job.machine?.modelDescription}</>} />
      </ListItem>

      <div className={classes.leftIndent}>
        <Typography>{job.machine?.machineNo}</Typography>
        <Typography>{job.machine?.modelType}</Typography>
        {job.machine?.idNo && <Typography>{job.machine.idNo}</Typography>}
        {job.machine?.location &&
          (job.machine.location.startsWith("///") ? (
            <Link
              href={`https://what3words.com/${job.machine.location.replace("///", "")}`}
              underline="none"
              color="blue"
              target="_blank"
              rel="noopener noreferrer"
            >
              {job.machine.location}
            </Link>
          ) : (
            <Typography>{job.machine.location}</Typography>
          ))}
        {machineInstallDate && (
          <Typography>
            {`${intl.formatMessage({
              id: "general.installed",
            })} ${intlFormatDate(intl, machineInstallDate)}`}
          </Typography>
        )}
        {machineSortGroupsEnabled && !!job.machine?.details && (
          <DetailAccordion
            data-testid="MachineDisplay-DetailAccordion"
            content={
              <>
                {!!job.machine.details.information && (
                  <Typography>
                    {`${intl.formatMessage({
                      id: "job.machine.details.information",
                    })}: ${job.machine.details.information}`}
                  </Typography>
                )}
                {!!job.machine.details.customProperties?.length && (
                  <Grid container>
                    <Grid item>
                      {job.machine.details.customProperties.map((cp, index) => (
                        <Typography key={`${cp?.code}-${index}`}>
                          {`${cp?.description}: ${formatCustomProperty(intl, cp, machineProperties)}`}
                        </Typography>
                      ))}
                    </Grid>
                  </Grid>
                )}
                {!!job.machine.details.sortGroups?.length &&
                  job.machine.details.sortGroups.map((sg, index) => (
                    <Typography key={`sortgroup-${index}`}>
                      {`${sg?.description}: ${sg?.value}`}
                    </Typography>
                  ))}
              </>
            }
          />
        )}
      </div>
    </>
  );
};
